import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/utils/formatters";
import type { AgreementFileDownload } from "@/schemas/agreement";
import { formatFileDownloadUser } from "../../hooks/useAgreementFileDownloads";

interface AgreementFileDownloadsDialogProps {
  /** Czy okno jest otwarte */
  open: boolean;
  /** Zamknięcie okna (klik poza, Esc, przycisk) */
  onClose: () => void;
  /** Nazwa pliku pokazywana pod tytułem */
  filename: string;
  /** Zdarzenia tego pliku rosnąco po dacie (kolejność z `useAgreementFileDownloads`) */
  entries: AgreementFileDownload[];
  /** Łączna liczba zdarzeń pliku (agregat); większa niż `entries.length`, gdy lista szczegółów jest przycięta */
  total: number;
}

// Na 360 px domyślne `p-4` zostawia kolumnom za mało miejsca.
const CELL_CLASS = "px-2 py-2 sm:px-4 sm:py-4";

/** Okno „Historia pobrań" jednego pliku umowy: tryb, data, użytkownik (rola) — rosnąco po dacie. */
export const AgreementFileDownloadsDialog = ({
  open,
  onClose,
  filename,
  entries,
  total,
}: AgreementFileDownloadsDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="min-w-0">
          <DialogTitle>
            {t("agreements.fileDownloads.historyTitle")}
          </DialogTitle>
          {/* Pełna nazwa zawinięta, nie ucięta: `title` nie działa na dotyku. */}
          <DialogDescription className="break-all">
            {filename}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-2 sm:px-4">
                  {t("agreements.fileDownloads.columns.mode")}
                </TableHead>
                <TableHead className="px-2 sm:px-4">
                  {t("agreements.fileDownloads.columns.date")}
                </TableHead>
                <TableHead className="px-2 sm:px-4">
                  {t("agreements.fileDownloads.columns.user")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className={`${CELL_CLASS} whitespace-nowrap`}>
                    {t(`agreements.fileDownloads.mode.${entry.mode}`)}
                  </TableCell>
                  <TableCell className={`${CELL_CLASS} whitespace-nowrap`}>
                    {formatDateTime(entry.downloaded_at)}
                  </TableCell>
                  <TableCell className={CELL_CLASS}>
                    {formatFileDownloadUser(entry)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {entries.length < total && (
          <p className="text-xs text-gray-600">
            {t("agreements.fileDownloads.historyTruncated", {
              shown: entries.length,
              total,
            })}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
